import { useEffect } from "react";

const DEFAULT_SUFFIX = " — NOVIFOOD";
const DEFAULT_OG_IMAGE = "/brand/og-default.jpg";

/**
 * Set a <meta> by name (or property if isProperty).
 * Creates the tag if missing, reuses & updates existing one with a data-managed flag.
 */
function setMeta(key, value, { isProperty = false } = {}) {
    if (!value) return;
    const attr = isProperty ? "property" : "name";
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-managed", "seo");
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
}

function setLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-managed", "seo");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJsonLd(id, data) {
    let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
    if (!data) {
        if (el) el.remove();
        return;
    }
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-seo-id", id);
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

/**
 * useSeo — declarative head metadata per page.
 *
 * @param {object} opts
 * @param {string} opts.title           Document title (suffix " — NOVIFOOD" added unless titleSuffix=false)
 * @param {string} opts.description     Meta description (≤ 160 chars recommended)
 * @param {string} [opts.image]         Absolute or root-relative og:image URL
 * @param {string} [opts.path]          Path used for canonical & og:url (defaults to current location)
 * @param {string} [opts.type]          og:type (default "website")
 * @param {boolean} [opts.titleSuffix]  Append " — NOVIFOOD" (default true)
 * @param {object} [opts.jsonLd]        Optional JSON-LD object
 * @param {string} [opts.jsonLdId]      Identifier for the JSON-LD script tag (default "page")
 */
export default function useSeo({
    title,
    description,
    image,
    path,
    type = "website",
    titleSuffix = true,
    jsonLd,
    jsonLdId = "page",
}) {
    useEffect(() => {
        if (typeof document === "undefined") return;

        const fullTitle = title
            ? titleSuffix && !title.includes("NOVIFOOD")
                ? `${title}${DEFAULT_SUFFIX}`
                : title
            : "NOVIFOOD — Carrinhos premium de food cart à medida em Portugal";

        document.title = fullTitle;

        const url =
            typeof window !== "undefined"
                ? `${window.location.origin}${path || window.location.pathname}`
                : null;
        const ogImage = image
            ? image.startsWith("http")
                ? image
                : `${typeof window !== "undefined" ? window.location.origin : ""}${image}`
            : `${typeof window !== "undefined" ? window.location.origin : ""}${DEFAULT_OG_IMAGE}`;

        if (description) setMeta("description", description);
        setMeta("og:title", fullTitle, { isProperty: true });
        if (description) setMeta("og:description", description, { isProperty: true });
        setMeta("og:type", type, { isProperty: true });
        if (url) setMeta("og:url", url, { isProperty: true });
        setMeta("og:image", ogImage, { isProperty: true });
        setMeta("og:locale", "pt_PT", { isProperty: true });
        setMeta("og:site_name", "NOVIFOOD", { isProperty: true });

        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", fullTitle);
        if (description) setMeta("twitter:description", description);
        setMeta("twitter:image", ogImage);

        if (url) setLink("canonical", url);

        if (jsonLd) {
            setJsonLd(jsonLdId, jsonLd);
        }

        return () => {
            // Remove only the per-page JSON-LD script to avoid stacking
            if (jsonLd) setJsonLd(jsonLdId, null);
        };
    }, [title, description, image, path, type, titleSuffix, jsonLd, jsonLdId]);
}
